import React from 'react';
import ProductPage from '../components/ProductPage';
import { AmmTaskFinder } from '../components/AmmTaskFinder';
import { getBoeingCoverage } from '../data/acousticCameras';

const FOTRIC_IMAGES = [
  'https://static.wixstatic.com/media/0fa391_bab168322b7042a898cae58409a8922a~mv2.png/v1/fill/w_540,h_910,al_c,q_85,enc_auto/0fa391_bab168322b7042a898cae58409a8922a~mv2.png',
  'https://static.wixstatic.com/media/0fa391_ca3e383e39024d818ea47623cf2ffdbe~mv2.png/v1/fill/w_540,h_860,al_c,q_85,enc_auto/0fa391_ca3e383e39024d818ea47623cf2ffdbe~mv2.png',
  'https://static.wixstatic.com/media/0fa391_b115b420cd9344248d5853e19468c198~mv2.png/v1/fill/w_720,h_800,al_c,q_85,enc_auto/0fa391_b115b420cd9344248d5853e19468c198~mv2.png',
];

const AcousticCameraH7Page = ({ onRequestDemo, onRequestQuote }) => {
  const productData = {
    productName: 'FOTRIC H7 Acoustic Imaging Camera',
    tagline: 'Powerful Upgrade — Long-Range Detection to 300 m',
    description:
      'The FOTRIC H7 is the flagship of the H-Series acoustic imaging line, purpose-built for long-range aviation and industrial inspections. A spiral-topology-optimized array of 345 MEMS digital microphones — the highest density in a handheld acoustic camera — delivers exceptional sensitivity and directivity, extending operating distance from 0.3 m all the way out to 300 m. Combined with the NaviPdM® Venus smart inspection assistant, the H7 enables a fully digitalized workflow from data acquisition through PD diagnosis, leak-cost evaluation, and one-tap report generation — replacing fragmented tools and manual data hand-offs.',
    image: FOTRIC_IMAGES[0],
    images: FOTRIC_IMAGES,
    imageContain: true,
    features: [
      {
        title: '345 MEMS Digital Microphone Array',
        description:
          'Spiral-topology-optimized array — the highest microphone density in the FOTRIC acoustic line — for the highest sensitivity and directivity available in a handheld camera.',
      },
      {
        title: 'Ultra-Long Detection Range: 0.3 – 300 m',
        description:
          'Operational distance extends from 1 ft close-in inspection out to 984 ft — enough to survey a tail-mounted APU, wingtip lights, or an elevated transmission tower from ground level.',
      },
      {
        title: 'NaviPdM® Venus Smart Inspection Assistant',
        description:
          'Cloud-connected inspection platform: QR-based asset creation, automatic data capture, PD diagnosis reports, and leak evaluation reports flow end-to-end without manual re-entry.',
      },
      {
        title: 'AI Partial Discharge Diagnosis',
        description:
          'Automatically identifies surface discharge, floating discharge, and corona discharge, and evaluates severity — engineered for aviation ground-support electrical inspections and hangar substation walks.',
      },
      {
        title: 'Smart Leak Cost Evaluation',
        description:
          'Automatically quantifies each detected leak in annual energy cost so maintenance decisions can be prioritized by dollar impact, not guesswork.',
      },
      {
        title: 'Acoustic Image Delay (T-FFTD®)',
        description:
          'Multi-modal transient acoustic matrix retains microsecond-level events so intermittent leaks and discharges stay visible long enough to confirm.',
      },
      {
        title: 'Acoustic Image Focus with Adaptive Noise Reduction',
        description:
          'Dynamic noise-reduction algorithms focus the display on a selected sound source and mask the rest — critical on a busy ramp or in a running engine bay.',
      },
      {
        title: 'Dual LED Illumination',
        description:
          'Dual-LED light source design delivers superior illumination for low-light hangars, night-shift ramp work, and inspection of shadowed structural pockets.',
      },
      {
        title: 'Extended-Runtime Battery System',
        description:
          '7.4 V, 3500 mAh field-replaceable Li-ion; ships with two packs for >6 hours of continuous inspection — enough for full-shift AOG work without a charge break.',
      },
      {
        title: '5" Gorilla Anti-Explosion Touchscreen',
        description:
          '1280 × 720 industrial LCD with Gorilla anti-explosion cover — clear in direct sunlight and rugged enough for the hangar floor.',
      },
      {
        title: 'On-Device Word/PDF Reports',
        description:
          'Finished inspection reports generate directly on the camera in Word or PDF, previewable in the gallery before hand-off.',
      },
      {
        title: 'Hot-Swappable 1 TB SD Storage',
        description:
          'Supports SD storage up to 1 TB with hot-swap capability — no lost data mid-inspection when the card fills.',
      },
    ],
    specifications: [
      { label: 'Microphone Channels', value: '345 MEMS digital microphones' },
      { label: 'Working Distance', value: '0.3 – 300 m (1 – 984 ft)' },
      { label: 'NaviPdM® Venus AI Assistant', value: 'Included' },
      { label: 'Acoustic Image FOV', value: '66° × 52°' },
      { label: 'Acoustic Sampling Rate', value: '200 kHz' },
      { label: 'Acoustic Refresh Rate', value: '25 Hz' },
      { label: 'Frequency Range', value: '2 – 100 kHz' },
      { label: 'Detection Modes', value: 'Leak mode (level); PD mode (PRPD, 50/60 Hz)' },
      { label: 'Partial Discharge Diagnostics', value: 'Surface, floating, corona — auto-diagnosis with severity' },
      { label: 'T-FFTD®', value: 'Captures instantaneous sound events with extended display retention' },
      { label: 'Acoustic Image Focus', value: 'Adaptive dynamic noise-reduction with source focus' },
      { label: 'On-Device Analysis', value: 'Direct on-camera image analysis' },
      { label: 'On-Device Report Generation', value: 'Word / PDF' },
      { label: 'Analysis Software', value: 'AnalyzIR — professional thermal + acoustic analysis' },
      { label: 'Display', value: '5" 1280 × 720 LCD, Gorilla anti-explosion' },
      { label: 'Image Modes', value: 'Single-source, multi-source, hologram' },
      { label: 'Digital Camera', value: '13 Megapixel industrial-grade' },
      { label: 'Storage Card', value: 'SD, hot-swappable, up to 1 TB' },
      { label: 'Battery', value: '7.4 V, 3500 mAh rechargeable Li-ion, field-replaceable (2 packs included)' },
      { label: 'Battery Runtime', value: '≥ 6 hours combined (≥ 3 h each pack)' },
      { label: 'Illumination', value: 'Dual LED light source' },
      { label: 'Supported Languages', value: 'English, French, German, Italian, Japanese, Korean, Portuguese, Spanish, Thai, Traditional Chinese' },
    ],
    applications: [
      'Long-range inspection of tail-mounted APUs and vertical stabilizers from ground level',
      'Wingtip navigation-light and beacon acoustic checks',
      'Ramp-level survey of elevated GPU cabinets and transformer bays',
      'Substation and switchgear PD diagnostics across the hangar electrical room',
      'Cabin pressurization and bleed-air leak inspections',
      'Landing gear well hydraulic and pneumatic acoustic surveys',
      'AOG response where the fault is at height or in a hard-to-reach section',
      'Compressed air system energy audits with automated cost reporting',
      'Fleet-wide PD trend monitoring via NaviPdM® Venus',
      'Nighttime and low-light hangar troubleshooting using dual-LED illumination',
    ],
    partNumber: 'FOTRIC-H7',
    boeingToolTag: 'COM-18702 (equivalent)',
    boeingCoverage: getBoeingCoverage('h7'),
    compareCameraId: 'h7',
  };

  return <ProductPage whereToBuy {...productData} extraSections={<AmmTaskFinder tool="COM-18702" />} onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />;
};

export default AcousticCameraH7Page;
